import React from 'react';
import { IQuestionListItem, QuestionType, IPaperModuleListItem, IUserAnswerMap } from '@/types';
import classNames from 'classnames';
import { isQuestionDone } from '@/utils';
import { QuestionMarkActiveIcon } from '@/svgs';
import './index.less';
import { useLottie } from 'lottie-react';
import animationData from '@/pages/prediction-score/predict.json';

const lottieOptions = {
    animationData: animationData,
    loop: true,
};

const lottieStyle: React.CSSProperties = {
    width: 20,
    height: 20,
    marginRight: 2,
};
import { useTranslation } from 'react-i18next';

interface AnswerSheetProps {
    moduleList: IPaperModuleListItem[];
    questionList: IQuestionListItem[];
    userAnswerMap: IUserAnswerMap;
    isAnalysis?: boolean;
    onChangeActiveQuestion?: (questionId: number, subQuestionId: number) => void;
    showModule?: boolean;
    markList?: number[];
    predictionExplain?: {
        predictionComment: string;
        predictionCount: number;
        showPopup: number;
    };
}

const AnswerSheet: React.FC<AnswerSheetProps> = props => {
    const {
        questionList,
        moduleList,
        isAnalysis = false,
        showModule = false,
        userAnswerMap,
        onChangeActiveQuestion,
        markList = [],
        predictionExplain,
    } = props;
    const { t } = useTranslation();

    const handleClick = (question: IQuestionListItem, comprehensiveQuestion?: IQuestionListItem) => {
        if (comprehensiveQuestion) {
            onChangeActiveQuestion?.(comprehensiveQuestion.questionId, question.questionId);
        } else {
            onChangeActiveQuestion?.(question.questionId, 0);
        }
    };

    const makeQuestionItem = (question: IQuestionListItem, comprehensiveQuestion?: IQuestionListItem) => {
        const { questionId } = question;
        const userAnswer = userAnswerMap[question.questionId];
        return (
            <button
                key={questionId}
                className={classNames('sub-tiku__answer-sheet-question-list-item', {
                    'sub-tiku__complete': isQuestionDone(userAnswer) && !isAnalysis,
                    'sub-tiku__error': userAnswer?.userAnswerStatus === 2 && isAnalysis,
                    'sub-tiku__uncompleted': userAnswer?.userAnswerStatus === 0 && isAnalysis,
                    'sub-tiku__correct': userAnswer?.userAnswerStatus === 1 && isAnalysis,
                    'sub-tiku__half-correct': userAnswer?.userAnswerStatus === 3 && isAnalysis,
                    'sub-tiku__subjective': userAnswer?.userAnswerStatus === 4 && isAnalysis,
                })}
                onClick={() => handleClick(question, comprehensiveQuestion)}
            >
                {markList.includes(questionId) && <QuestionMarkActiveIcon />}
                <span>{question.sortNum}</span>
            </button>
        );
    };

    const makeModuleQuestionList = () => {
        if (!moduleList?.length) return null;
        const doms = [];
        for (let index = 0; index < moduleList.length; index++) {
            const moduleItem = moduleList[index];
            if (!moduleItem.questionList?.length) continue;

            doms.push(
                <div className="sub-tiku__answer-sheet-module" key={`${index + 1}`}>
                    <div className="sub-tiku__answer-sheet-module-item">
                        <div className="module-title">{moduleItem.title}</div>
                    </div>
                    <div className="sub-tiku__answer-sheet-question-list">
                        {moduleItem.questionList.map(question => {
                            if (question.questionType === QuestionType.comprehensive) {
                                return question.subQuestionList?.map(subQuestion => makeQuestionItem(subQuestion, question));
                            }
                            return makeQuestionItem(question);
                        })}
                    </div>
                </div>
            );
        }
        return doms;
    };

    const makeQuestionList = () => {
        if (!questionList?.length) return null;
        return (
            <div className="sub-tiku__answer-sheet-question-list">
                {questionList.map(question => {
                    if (question.questionType === QuestionType.comprehensive) {
                        return question.subQuestionList?.map(subQuestion => makeQuestionItem(subQuestion, question));
                    }
                    return makeQuestionItem(question);
                })}
            </div>
        );
    };

    const { View } = useLottie(lottieOptions, lottieStyle);

    return (
        <div>
            <div className="sub-tiku__answer-sheet">
                <div className="sub-tiku__answer-sheet-title">
                    <span>{t('paper.answerSheet')}</span>
                    <div className="sub-tiku__answer-sheet-tip">
                        {isAnalysis ? (
                            <>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-correct"></i>
                                    <span>{t('paper.correct')}</span>
                                </div>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-half-correct"></i>
                                    <span>{t('paper.halfCorrect')}</span>
                                </div>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-error"></i>
                                    <span>{t('paper.error')}</span>
                                </div>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-subjective"></i>
                                    <span>{t('paper.subjective')}</span>
                                </div>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-uncompleted"></i>
                                    <span>{t('paper.uncompleted')}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-complete"></i>
                                    <span>{t('paper.completed')}</span>
                                </div>
                                <div className='sub-tiku__answer-sheet-tip-item'>
                                    <i className="sub-tiku__icon-uncompleted"></i>
                                    <span>{t('paper.uncompleted')}</span>
                                </div>
                            </>
                        )}
                    </div>
                    {predictionExplain?.showPopup === 1 && (
                        <div className="sub-tiku__answer-sheet-predict-explain">
                            {View}
                            <span dangerouslySetInnerHTML={{ __html: predictionExplain?.predictionComment }} />
                        </div>
                    )}
                </div>
                <div className={classNames('sub-tiku__answer-sheet-content')}>
                    <div className={classNames('sub-tiku__answer-sheet-scroll')}>
                        {showModule ? makeModuleQuestionList() : makeQuestionList()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnswerSheet;
